import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import {
  listWorkspacesForUser,
  getActiveWorkspaceId,
  setActiveWorkspaceId,
  createWorkspace as createWorkspaceRecord,
  updateWorkspace as updateWorkspaceRecord,
  deleteWorkspace as deleteWorkspaceRecord,
  setWorkspaceMembers as setWorkspaceMembersApi,
  uploadWorkspaceLogo as uploadWorkspaceLogoApi,
  deleteWorkspaceLogo as deleteWorkspaceLogoApi,
  normalizeWorkspaceEmail,
} from "@/api/workspacesApi";
import { getWorkspaceBootstrap } from "@/api/rbacSettingsApi";
import { setInboxAgentWorkspaceId } from "@/lib/inboxLiveConfig";
import { setReportingApiWorkspaceId } from "@/lib/reportingApi";
import { setTenantApiWorkspaceId } from "@/lib/tenantApi";
import { SessionExpiredError } from "@/lib/sessionExpiredEvent";
import { WorkspaceThemePreferencesLoader } from "@/lib/workspaceThemePreferencesLoader";
import { useTheme } from "@/contexts/ThemeContext";

const WorkspaceContext = createContext(null);
const WORKSPACE_LOAD_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 12000];
// Transient statuses we recover from silently (keep showing "Loading…", never
// the "Unable to load workspaces" screen): gateway errors, plus a false/blip
// 401 (fetchWithAuthRetry already tried to refresh) and a transient 500 (e.g.
// an upstream introspection hiccup). A network error (no numeric status) is
// also treated as transient.
const TRANSIENT_WORKSPACE_LOAD_STATUSES = new Set([401, 500, 502, 503, 504]);

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientWorkspaceLoadError(error) {
  // A definitive session expiry already dispatched the session-expired modal —
  // don't retry it, let it settle.
  if (error instanceof SessionExpiredError) return false;
  const status = error && typeof error === "object" ? Number(error.status) : NaN;
  if (Number.isNaN(status)) return true; // network / unknown → retry silently
  return TRANSIENT_WORKSPACE_LOAD_STATUSES.has(status);
}

async function retryTransientWorkspaceLoad(fn) {
  let lastError;
  for (let attempt = 0; attempt <= WORKSPACE_LOAD_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isTransientWorkspaceLoadError(error) || attempt === WORKSPACE_LOAD_RETRY_DELAYS_MS.length) {
        throw error;
      }
      await delay(WORKSPACE_LOAD_RETRY_DELAYS_MS[attempt]);
    }
  }
  throw lastError;
}

const themeLoader = new WorkspaceThemePreferencesLoader();

export function WorkspaceProvider({ children }) {
  const { user, isAuthenticated, isInitializing } = useAuthContext();
  const { setTheme } = useTheme();
  const userEmail = user?.email ?? null;

  // Seed from the login-response default_workspace_id so X-Workspace-Id is set
  // on the very first API call, before GET /workspaces/active resolves.
  const sessionDefaultWorkspaceId =
    user?.default_workspace_id && String(user.default_workspace_id).trim()
      ? String(user.default_workspace_id).trim()
      : null;

  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState(sessionDefaultWorkspaceId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(/** @type {Error | null} */ (null));

  const applyWorkspaceScope = useCallback((workspaceId) => {
    const id = workspaceId && String(workspaceId).trim() ? String(workspaceId).trim() : null;
    setTenantApiWorkspaceId(id);
    setReportingApiWorkspaceId(id);
    setInboxAgentWorkspaceId(id);
    setActiveWorkspaceIdState(id);
  }, []);

  const load = useCallback(async () => {
    if (!userEmail) {
      setWorkspaces([]);
      applyWorkspaceScope(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { list, nextId } = await retryTransientWorkspaceLoad(async () => {
        const { data: listData } = await listWorkspacesForUser({ userEmail });
        let workspaceList = listData.workspaces;

        // Login token may already name the default workspace before membership heal completes.
        const sessionDefault = sessionDefaultWorkspaceId;
        const allowlist = Array.isArray(user?.workspace_client_allowlist)
          ? user.workspace_client_allowlist.map(String)
          : [];
        if (
          workspaceList.length === 0 &&
          sessionDefault &&
          (allowlist.length === 0 || allowlist.includes(sessionDefault))
        ) {
          workspaceList = [
            {
              id: sessionDefault,
              name: "Default",
              slug: "default",
              isDefault: true,
            },
          ];
        }

        let bootstrapDefault = "";
        try {
          const boot = await getWorkspaceBootstrap();
          const order = Array.isArray(boot?.workspaceIds) ? boot.workspaceIds : [];
          if (order.length > 0) {
            const rank = new Map(order.map((id, i) => [String(id), i]));
            const filtered = workspaceList
              .filter((w) => rank.has(String(w.id)))
              .sort((a, b) => rank.get(String(a.id)) - rank.get(String(b.id)));
            // Keep DB membership when bootstrap order does not overlap (stale JWT / KC lag).
            if (filtered.length > 0) {
              workspaceList = filtered;
            }
          }
          if (boot?.defaultWorkspaceId != null) {
            bootstrapDefault = String(boot.defaultWorkspaceId).trim();
          }
        } catch {
          /* keep full list if bootstrap is unavailable */
        }

        const { data: activeData } = await getActiveWorkspaceId({ userEmail });
        let resolvedId = activeData.workspaceId;
        const allowed = new Set(workspaceList.map((w) => w.id));
        // The stored choice wins so switching workspace survives a reload. Bootstrap's
        // default is the tenant's `is_default` workspace, not this user's pick —
        // preferring it would silently pull the agent back and swap their
        // workspace-scoped settings (theme included) on every load.
        if (!resolvedId || !allowed.has(resolvedId)) {
          resolvedId =
            bootstrapDefault && allowed.has(bootstrapDefault)
              ? bootstrapDefault
              : workspaceList[0]?.id ?? null;
        }
        if (resolvedId && resolvedId !== activeData.workspaceId) {
          await setActiveWorkspaceId({ userEmail, workspaceId: resolvedId });
        }
        return { list: workspaceList, nextId: resolvedId };
      });
      setWorkspaces(list);
      applyWorkspaceScope(nextId);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [userEmail, user, sessionDefaultWorkspaceId, applyWorkspaceScope]);

  useEffect(() => {
    if (isInitializing) return;
    if (!isAuthenticated || !userEmail) {
      setWorkspaces([]);
      applyWorkspaceScope(null);
      // Keep loading=true so the next login doesn't flash an empty workspace state
      // before the load effect fires. Loading will be set to false after the first
      // authenticated load completes (success or error).
      setLoading(true);
      setError(null);
      return;
    }
    // Pre-seed active workspace from session so API calls don't fire with null header.
    if (sessionDefaultWorkspaceId) {
      applyWorkspaceScope(sessionDefaultWorkspaceId);
    }
    void load();
  }, [isAuthenticated, isInitializing, userEmail, load, sessionDefaultWorkspaceId, applyWorkspaceScope]);

  useEffect(() => {
    setTenantApiWorkspaceId(activeWorkspaceId);
    setReportingApiWorkspaceId(activeWorkspaceId);
    setInboxAgentWorkspaceId(activeWorkspaceId);
  }, [activeWorkspaceId]);

  useEffect(() => {
    if (!activeWorkspaceId || !isAuthenticated) return;
    void themeLoader.loadAndApply(setTheme);
  }, [activeWorkspaceId, isAuthenticated, setTheme]);

  const switchWorkspace = useCallback(
    async (workspaceId) => {
      if (!userEmail) return;
      const allowed = new Set(workspaces.map((w) => w.id));
      if (!allowed.has(workspaceId)) return;
      await setActiveWorkspaceId({ userEmail, workspaceId });
      applyWorkspaceScope(workspaceId);
    },
    [userEmail, workspaces, applyWorkspaceScope]
  );

  const createWorkspace = useCallback(
    async ({ name, businessUnit, memberEmails, inviteEmails }) => {
      if (!userEmail) throw new Error("Not signed in.");
      const { data } = await createWorkspaceRecord({
        userEmail,
        name,
        businessUnit,
        memberEmails: memberEmails ?? [],
        inviteEmails: inviteEmails ?? [],
      });
      await setActiveWorkspaceId({ userEmail, workspaceId: data.workspace.id });
      await load();
      const failed = data.members?.failed || [];
      if (failed.length) {
        const names = failed.map((row) => row.email).filter(Boolean).join(", ");
        throw new Error(
          `Workspace created, but these users could not be added or invited: ${names || "one or more emails"}.`
        );
      }
      return data.workspace;
    },
    [userEmail, load]
  );

  const assignWorkspaceMembers = useCallback(
    async ({ workspaceId, userEmails }) => {
      if (!userEmail) throw new Error("Not signed in.");
      const actor = normalizeWorkspaceEmail(userEmail);
      const merged = new Set((userEmails || []).map(normalizeWorkspaceEmail).filter(Boolean));
      if (actor) merged.add(actor);
      const result = await setWorkspaceMembersApi({ workspaceId, userEmails: [...merged] });
      await load();
      const failed = result.data?.members?.failed || [];
      if (failed.length) {
        const names = failed.map((row) => row.email).filter(Boolean).join(", ");
        throw new Error(`Some users could not be provisioned: ${names || "one or more emails"}.`);
      }
    },
    [userEmail, load]
  );

  const renameWorkspace = useCallback(
    async ({ workspaceId, name, businessUnit }) => {
      await updateWorkspaceRecord({ workspaceId, name, businessUnit });
      await load();
    },
    [load]
  );

  const deleteWorkspace = useCallback(
    async ({ workspaceId }) => {
      if (!userEmail) throw new Error("Not signed in.");
      const deletedId = String(workspaceId || "").trim();
      if (!deletedId) throw new Error("Workspace id required.");

      const remaining = workspaces.filter((w) => w.id !== deletedId);
      const nextId = remaining[0]?.id ?? null;

      await deleteWorkspaceRecord({ workspaceId: deletedId });

      if (nextId) {
        await setActiveWorkspaceId({ userEmail, workspaceId: nextId });
        applyWorkspaceScope(nextId);
      } else {
        applyWorkspaceScope(null);
      }
      await load();
    },
    [userEmail, workspaces, applyWorkspaceScope, load]
  );

  const uploadLogo = useCallback(
    async ({ workspaceId, file }) => {
      const result = await uploadWorkspaceLogoApi({ workspaceId, file });
      setWorkspaces((prev) =>
        prev.map((w) => (w.id === workspaceId ? { ...w, logoUrl: result.logoUrl ?? w.logoUrl } : w)),
      );
      await load();
      return result;
    },
    [load]
  );

  const removeLogo = useCallback(
    async ({ workspaceId }) => {
      await deleteWorkspaceLogoApi({ workspaceId });
      setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? { ...w, logoUrl: null } : w)));
      await load();
    },
    [load]
  );

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId]
  );

  const value = useMemo(
    () => ({
      workspaces,
      activeWorkspace,
      activeWorkspaceId,
      loading,
      error,
      refresh: load,
      switchWorkspace,
      createWorkspace,
      assignWorkspaceMembers,
      renameWorkspace,
      deleteWorkspace,
      uploadLogo,
      removeLogo,
    }),
    [
      workspaces,
      activeWorkspace,
      activeWorkspaceId,
      loading,
      error,
      load,
      switchWorkspace,
      createWorkspace,
      assignWorkspaceMembers,
      renameWorkspace,
      deleteWorkspace,
      uploadLogo,
      removeLogo,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with provider for workspace scope
export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return ctx;
}
